import React, { useState, useEffect, useContext } from "react";
import {
  Bell, Check, CheckCheck, Trash2, X,
  Calendar, TrendingUp, CreditCard, Star,
  DoorOpen, AlertTriangle, Users, HelpCircle,
} from "lucide-react";
import axios from "axios";
import { Container, Row, Col, Card, Badge, Button, ListGroup } from "react-bootstrap";
import AppContext from "../../context/AppContext";
import VendorLayout from "../../layout/HotelVendorLayout/VendorLayout";
import "../../assets/css/vendors/HotelScreens/hotelNotifications.css";

const HotelNotifications = () => {
  const { authToken, baseUrl, vendorId } = useContext(AppContext);

  const [notifications, setNotifications] = useState([]);
  const [loadingList, setLoadingList]     = useState(true);
  const [fetchError, setFetchError]       = useState("");

  useEffect(() => {
    if (!vendorId) {
      setFetchError("Vendor not identified. Please log in again.");
      setLoadingList(false);
      return;
    }

    setLoadingList(true);
    setFetchError("");

    axios
      .get(`${baseUrl}/vendor/notifications`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params:  { vendor_id: vendorId },
      })
      .then((res) => {
        const raw    = res.data?.data || [];
        const mapped = raw.map((n) => ({
          id:      n.id,
          title:   n.notification_title       || "Notification",
          message: n.notification_description || "",
          read:    n.status === "read",
          time:    n.created_at
                     ? new Date(n.created_at).toLocaleString("en-US", {
                         month: "short", day: "numeric",
                         hour: "2-digit", minute: "2-digit",
                       })
                     : "",
          type: n.type || "general",
        }));
        setNotifications(mapped);
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setFetchError("Failed to load notifications.");
      })
      .finally(() => setLoadingList(false));
  }, [vendorId]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const getNotificationIcon = (type) => {
    const icons = {
      booking:      <Calendar size={20} />,
      payment:      <CreditCard size={20} />,
      cancellation: <X size={20} />,
      review:       <Star size={20} />,
      checkout:     <DoorOpen size={20} />,
      alert:        <AlertTriangle size={20} />,
      earnings:     <TrendingUp size={20} />,
      hr:           <Users size={20} />,
      support:      <HelpCircle size={20} />,
    };
    return icons[type] || <Bell size={20} />;
  };

  const getIconBgColor = (type) => {
    const colors = {
      booking:      "#4A9E8E",
      payment:      "#28a745",
      cancellation: "#dc3545",
      review:       "#FFB800",
      checkout:     "#6c757d",
      alert:        "#fd7e14",
      earnings:     "#2C5F7C",
      hr:           "#6610f2",
      support:      "#4A9E8E",
    };
    return colors[type] || "#6c757d";
  };

  if (loadingList) {
    return (
      <VendorLayout>
        <Container fluid className="hotel-notifications-container">
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading notifications...</p>
          </div>
        </Container>
      </VendorLayout>
    );
  }

  if (fetchError) {
    return (
      <VendorLayout>
        <Container fluid className="hotel-notifications-container">
          <div className="text-center p-5 text-danger">
            <p>{fetchError}</p>
          </div>
        </Container>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <Container fluid className="hotel-notifications-container">
        <div className="hotel-notifications-wrapper">
          <Card className="hotel-notifications-header-card">
            <Card.Body className="hotel-notifications-header-body">
              <Row className="hotel-notifications-header-row">
                <Col md={6} className="hotel-notifications-header-col mb-3 mb-md-0">
                  <div className="hotel-notifications-header-content">
                    <div className="hotel-notifications-bell-wrapper">
                      <Bell size={24} />
                      {unreadCount > 0 && (
                        <Badge bg="danger" className="hotel-notifications-badge" pill>
                          {unreadCount}
                        </Badge>
                      )}
                    </div>
                    <div className="hotel-notifications-title-wrapper">
                      <h2 className="hotel-notifications-main-title">Notifications</h2>
                      <p className="hotel-notifications-subtitle">
                        {unreadCount > 0 ? `${unreadCount} unread` : "All caught up!"}
                      </p>
                    </div>
                  </div>
                </Col>

                <Col md={6} className="hotel-notifications-actions-col">
                  <div className="hotel-notifications-actions-wrapper">

                    {unreadCount > 0 && (
                      <Button
                        variant="primary"
                        className="hotel-notifications-mark-all-btn"
                        onClick={() =>
                          axios
                            .post(
                              `${baseUrl}/vendor/notifications-read-all`,
                              { vendor_id: vendorId },
                              { headers: { Authorization: `Bearer ${authToken}` } }
                            )
                            .then((res) => {
                              if (res.data.success) {
                                setNotifications((prev) =>
                                  prev.map((n) => ({ ...n, read: true }))
                                );
                              }
                            })
                            .catch((err) => console.error("Mark all read error:", err))
                        }
                      >
                        <CheckCheck size={16} className="hotel-notifications-btn-icon" />
                        Mark All Read
                      </Button>
                    )}

                    {notifications.some((n) => n.read) && (
                      <Button
                        variant="warning"
                        className="hotel-notifications-clear-read-btn"
                        onClick={() =>
                          axios
                            .delete(
                              `${baseUrl}/vendor/notifications-delete-read`,
                              {
                                headers: { Authorization: `Bearer ${authToken}` },
                                data:    { vendor_id: vendorId },
                              }
                            )
                            .then((res) => {
                              if (res.data.success) {
                                setNotifications((prev) =>
                                  prev.filter((n) => !n.read)
                                );
                              }
                            })
                            .catch((err) => console.error("Delete all read error:", err))
                        }
                      >
                        <Trash2 size={16} className="hotel-notifications-btn-icon" />
                        Clear Read
                      </Button>
                    )}

                  </div>
                </Col>

              </Row>
            </Card.Body>
          </Card>

          {notifications.length === 0 ? (
            <Card className="hotel-notifications-empty-card">
              <Card.Body className="hotel-notifications-empty-body">
                <Bell size={48} className="hotel-notifications-empty-icon" />
                <h4 className="hotel-notifications-empty-title">No notifications</h4>
                <p className="hotel-notifications-empty-text">You're all caught up!</p>
              </Card.Body>
            </Card>
          ) : (
            <ListGroup className="hotel-notifications-list">
              {notifications.map((notification) => (
                <ListGroup.Item
                  key={notification.id}
                  className={`hotel-notification-item ${
                    notification.read ? "" : "hotel-notification-unread"
                  }`}
                  style={{
                    borderLeftColor: getIconBgColor(notification.type),
                    cursor: "default",
                  }}
                >
                  <div className="hotel-notification-content">

                    <div className="hotel-notification-icon-wrapper">
                      <div
                        className="hotel-notification-icon"
                        style={{ backgroundColor: getIconBgColor(notification.type) }}
                      >
                        {getNotificationIcon(notification.type)}
                      </div>
                    </div>

                    <div className="hotel-notification-text-wrapper">
                      <div className="hotel-notification-text-header">

                        <div className="hotel-notification-title-wrapper">
                          <h5 className="hotel-notification-title">
                            {notification.title}
                          </h5>
                          {!notification.read && (
                            <Badge bg="warning" pill className="hotel-notification-unread-indicator">
                              ●
                            </Badge>
                          )}
                        </div>

                        <div className="hotel-notification-actions">
                          {!notification.read && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="hotel-notification-mark-read-btn"
                              onClick={() =>
                                axios
                                  .post(
                                    `${baseUrl}/vendor/notification-read`,
                                    { id: notification.id },
                                    { headers: { Authorization: `Bearer ${authToken}` } }
                                  )
                                  .then((res) => {
                                    if (res.data.success) {
                                      setNotifications((prev) =>
                                        prev.map((n) =>
                                          n.id === notification.id
                                            ? { ...n, read: true }
                                            : n
                                        )
                                      );
                                    }
                                  })
                                  .catch((err) => console.error("Mark read error:", err))
                              }
                            >
                              <Check size={16} />
                            </Button>
                          )}
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="hotel-notification-delete-btn"
                            onClick={() =>
                              axios
                                .delete(
                                  `${baseUrl}/vendor/notification-delete`,
                                  {
                                    headers: { Authorization: `Bearer ${authToken}` },
                                    data:    { id: notification.id },
                                  }
                                )
                                .then((res) => {
                                  if (res.data.success) {
                                    setNotifications((prev) =>
                                      prev.filter((n) => n.id !== notification.id)
                                    );
                                  }
                                })
                                .catch((err) => console.error("Delete error:", err))
                            }
                          >
                            <X size={16} />
                          </Button>

                        </div>
                      </div>

                      <p className="hotel-notification-message">
                        {notification.message}
                      </p>
                      <small className="hotel-notification-time">
                        {notification.time}
                      </small>

                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>
      </Container>
    </VendorLayout>
  );
};

export default HotelNotifications;